import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../request";
import { URL } from "../api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const useQuery = () => new URLSearchParams(useLocation().search);

const MandateNotifications = () => {
  const query = useQuery();
  const customerId = query.get("customerId");
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [executingId, setExecutingId] = useState(null);

  const handleExecute = async (n, parsed) => {
    try {
      setExecutingId(n.id);
      const formData = new URLSearchParams();
      formData.append("merchantRequestId", n.merchantRequestId);
      formData.append("amount", parsed.amount || "");
      formData.append("notificationMerchantRequestId", n.notificationMerchantRequestId);

      const res = await axiosInstance.post(URL.MANDATE_EXECUTE, formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      let payload = {};
      try { payload = JSON.parse(res.data?.responseJson || "{}").payload || {}; } catch {}

      const msg = payload.gatewayResponseMessage || "Execution sent";
      const status = payload.gatewayResponseStatus || res.data?.status || "";
      toast.success(`${msg} (${status})`);
      fetchNotifications();
    } catch (err) {
      console.error("Execute failed", err);
      toast.error(err?.response?.data?.message || "Failed to execute mandate");
    } finally {
      setExecutingId(null);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(URL.MANDATE_NOTIFICATIONS, {
        params: { customerId },
      });
      setData(res.data);
    } catch (err) {
      console.error("Notifications fetch failed", err);
      toast.error("Failed to fetch notifications");
      setData({ count: 0, customerId, notifications: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) fetchNotifications();
  }, [customerId]);

  const statusBadge = (status) => {
    const colors = {
      SUCCESS: "bg-success",
      PENDING: "bg-warning text-dark",
      FAILED: "bg-danger",
      INITIATED: "bg-info text-dark",
    };
    return colors[status] || "bg-secondary";
  };

  const formatJson = (str) => {
    try {
      return JSON.stringify(JSON.parse(str), null, 2);
    } catch {
      return str;
    }
  };

  const parseResponse = (str) => {
    try {
      return JSON.parse(str) || {};
    } catch {
      return {};
    }
  };

  const formatDate = (s) => {
    if (!s) return "-";
    try {
      return new Date(s).toLocaleString();
    } catch {
      return s;
    }
  };

  if (!customerId) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">No customer ID provided in URL.</div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <ToastContainer />

      {/* Header */}
      <div
        className="card mb-4 border-0 shadow-sm"
        style={{
          background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
          color: "#fff",
        }}
      >
        <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <div className="d-flex gap-2 mb-2">
              <button
                className="btn btn-sm btn-light"
                onClick={() => navigate(-1)}
              >
                <i className="bi bi-arrow-left me-1"></i>Back
              </button>
              {data?.notifications?.[0]?.merchantRequestId && (
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() =>
                    navigate(
                      `/RecurringPayments?merchantRequestId=${data.notifications[0].merchantRequestId}`
                    )
                  }
                >
                  <i className="bi bi-eye-fill me-1"></i>View Executions
                </button>
              )}
            </div>
            <h4 className="mb-0 fw-bold">
              <i className="bi bi-bell-fill me-2"></i>UPI AutoPay Notifications
            </h4>
            <small>Customer ID: {customerId}</small>
          </div>
          <div
            className="text-center"
            style={{
              background: "rgba(255,255,255,0.2)",
              borderRadius: 12,
              padding: "12px 24px",
              minWidth: 100,
            }}
          >
            <div style={{ fontSize: 32, fontWeight: "bold", lineHeight: 1 }}>
              {data?.count ?? 0}
            </div>
            <small>Notifications</small>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2 text-muted mb-0">Loading notifications...</p>
        </div>
      )}

      {!loading && data?.notifications?.length === 0 && (
        <div className="text-center py-5">
          <i className="bi bi-inbox" style={{ fontSize: 60, color: "#cbd3da" }}></i>
          <h5 className="mt-3 text-muted">No notifications found</h5>
          <p className="text-muted mb-0">This customer has no notification history yet.</p>
        </div>
      )}

      {!loading && data?.notifications?.length > 0 && (
        <div className="row g-3">
          {data.notifications.map((n) => {
            const isExpanded = expandedId === n.id;
            const parsed = parseResponse(n.responseJson);
            return (
              <div className="col-12" key={n.id}>
                <div
                  className="card shadow-sm border-0"
                  style={{ borderRadius: 10, overflow: "hidden" }}
                >
                  <div
                    className="card-header d-flex justify-content-between align-items-center"
                    style={{ background: "#f8f9fc", borderBottom: "1px solid #e3e6f0" }}
                  >
                    <div className="d-flex align-items-center">
                      <span className={`badge ${statusBadge(n.status)} px-3 py-2 me-2`}>
                        {n.status}
                      </span>
                      <strong style={{ fontSize: 14 }}>#{n.id}</strong>
                      <small className="text-muted ms-3">
                        <i className="bi bi-clock me-1"></i>
                        {formatDate(n.createdAt)}
                      </small>
                    </div>
                    <div className="d-flex gap-2">
                      {(() => {
                        const exec = parsed.nextExecution ? new Date(parsed.nextExecution) : null;
                        const isDue = exec && exec.getTime() <= Date.now();
                        const isExecuting = executingId === n.id;
                        return (
                          <button
                            className={`btn btn-sm ${isDue ? "btn-success" : "btn-secondary"}`}
                            disabled={!isDue || isExecuting}
                            onClick={() => handleExecute(n, parsed)}
                            title={isDue ? "Execute now" : "Not due yet"}
                            style={{ opacity: isDue ? 1 : 0.5 }}
                          >
                            <i className="bi bi-lightning-charge-fill me-1"></i>
                            {isExecuting ? "Executing..." : "Execute"}
                          </button>
                        );
                      })()}
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setExpandedId(isExpanded ? null : n.id)}
                      >
                        {isExpanded ? "Hide Details" : "View Details"}
                        <i className={`bi bi-chevron-${isExpanded ? "up" : "down"} ms-1`}></i>
                      </button>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <small className="text-muted d-block">Merchant Request ID</small>
                        <strong style={{ fontSize: 13, wordBreak: "break-all" }}>
                          {n.merchantRequestId || "-"}
                        </strong>
                      </div>
                      <div className="col-md-6">
                        <small className="text-muted d-block">Notification Request ID</small>
                        <strong style={{ fontSize: 13, wordBreak: "break-all" }}>
                          {n.notificationMerchantRequestId || "-"}
                        </strong>
                      </div>
                      <div className="col-md-6">
                        <small className="text-muted d-block">UMN</small>
                        <strong style={{ fontSize: 13, wordBreak: "break-all" }}>
                          {n.umn || "-"}
                        </strong>
                      </div>
                      <div className="col-md-3">
                        <small className="text-muted d-block">Amount</small>
                        <strong style={{ fontSize: 13, color: "#16a34a" }}>
                          {parsed.amount ? `₹${Number(parsed.amount).toFixed(2)}` : "-"}
                        </strong>
                      </div>
                      <div className="col-md-3">
                        <small className="text-muted d-block">Execution Date</small>
                        <strong style={{ fontSize: 13 }}>
                          {parsed.nextExecution ? formatDate(parsed.nextExecution) : "-"}
                        </strong>
                      </div>
                      <div className="col-md-3">
                        <small className="text-muted d-block">API Type</small>
                        <strong style={{ fontSize: 13 }}>{n.apiType || "-"}</strong>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3">
                        <small className="text-muted d-block mb-1">
                          <i className="bi bi-code-slash me-1"></i>Response JSON
                        </small>
                        <pre
                          style={{
                            background: "#1e293b",
                            color: "#e2e8f0",
                            padding: 16,
                            borderRadius: 8,
                            fontSize: 12,
                            maxHeight: 400,
                            overflow: "auto",
                            margin: 0,
                          }}
                        >
                          {formatJson(n.responseJson)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MandateNotifications;
